import { POLLINATOR_GARDEN_PRODUCTION_READY } from './PollinatorGardenAssets.js';
import { validateRelationshipInvestigationConfig } from '../minigames/relationship/RelationshipInvestigationConfig.js';

export const POLLINATOR_GARDEN_INVESTIGATION = validateRelationshipInvestigationConfig({
  id: 'pollinator-garden-1',
  masteryKey: 'science.plant-animal-relationships.pollination-evidence',
  phenomenon: 'pollination',
  title: 'The Missing Garden Visits',
  sceneObservation:
    'Two flower patches are blooming. Animals visit one patch often, while the other patch has very few visits and is making fewer seed pods.',
  predictionPrompt:
    'Choose the patch you want to watch for signs that flower visits help the plants make seeds.',
  organisms: [
    { id: 'flowering-plant', displayName: 'flowering plant', role: 'plant' },
    { id: 'bee', displayName: 'bee', role: 'animal' },
    { id: 'butterfly', displayName: 'butterfly', role: 'animal' },
  ],
  conditions: [
    {
      id: 'open-to-pollinators',
      label: 'Open flower patch',
      observationFocus: 'Watch animals land on flowers and move between blossoms.',
    },
    {
      id: 'limited-pollinator-visits',
      label: 'Quiet flower patch',
      observationFocus: 'Watch what happens when very few animals visit the flowers.',
    },
  ],
  interactions: [
    {
      id: 'inspect-open-patch',
      conditionId: 'open-to-pollinators',
      actorOrganismIds: ['flowering-plant'],
      observation: 'Several blossoms are open in the sunny patch.',
    },
    {
      id: 'bee-collects-pollen',
      conditionId: 'open-to-pollinators',
      actorOrganismIds: ['bee', 'flowering-plant'],
      observation: 'A bee lands on a flower, and grains of pollen cling to its body.',
    },
    {
      id: 'bee-carries-pollen',
      conditionId: 'open-to-pollinators',
      actorOrganismIds: ['bee', 'flowering-plant'],
      observation: 'The bee visits another flower and carries pollen between the blossoms.',
    },
    {
      id: 'butterfly-visits-flower',
      conditionId: 'open-to-pollinators',
      actorOrganismIds: ['butterfly', 'flowering-plant'],
      observation: 'A butterfly also stops at more than one flower.',
    },
    {
      id: 'inspect-quiet-patch',
      conditionId: 'limited-pollinator-visits',
      actorOrganismIds: ['flowering-plant'],
      observation: 'The quiet patch has flowers, but very few animal visits occur.',
    },
    {
      id: 'compare-seed-pods',
      conditionId: 'open-to-pollinators',
      actorOrganismIds: ['flowering-plant'],
      observation: 'Later, the often-visited patch has more new seed pods than the quiet patch.',
    },
  ],
  requiredInteractionIds: [
    'inspect-open-patch',
    'bee-collects-pollen',
    'bee-carries-pollen',
    'butterfly-visits-flower',
    'inspect-quiet-patch',
    'compare-seed-pods',
  ],
  evidenceTargets: [
    {
      id: 'animals-visited-flowers',
      label: 'Animals visited several flowers in the open patch.',
      sourceInteractionIds: ['bee-collects-pollen', 'butterfly-visits-flower'],
    },
    {
      id: 'pollen-moved-between-flowers',
      label: 'Pollen moved with the bee from one flower to another.',
      sourceInteractionIds: ['bee-collects-pollen', 'bee-carries-pollen'],
    },
    {
      id: 'visited-patch-made-more-seeds',
      label: 'The often-visited patch later had more new seed pods.',
      sourceInteractionIds: ['inspect-quiet-patch', 'compare-seed-pods'],
    },
  ],
  requiredEvidenceIds: [
    'animals-visited-flowers',
    'pollen-moved-between-flowers',
    'visited-patch-made-more-seeds',
  ],
  comparisonPrompt:
    'Place the visit, pollen, and seed-pod evidence together. What pattern do they show?',
  supportedConditionId: 'open-to-pollinators',
  unsupportedComparisonFeedback:
    'That patch had very few animal visits. Move the evidence to the patch where animals carried pollen between flowers and more seed pods appeared.',
  evidenceStatement:
    'The patch with animal visits had pollen carried between flowers and later made more seed pods. This model shows that flowering plants may depend on animal pollinators.',
  applicationPrompt:
    'Use the evidence to reopen a safe pollinator route through the garden, then observe the flowers and seed pods respond.',
  conclusionId: 'animal-visits-support-pollination',
  completionWorldEvent: 'pollinator-garden-restored',
  hints: {
    observe: ['Look closely at both flower patches. What is different about the visits?'],
    predict: ['Choose a patch to watch for animal movement, pollen, and later seed pods.'],
    investigate: [
      'Watch which animals visit the flowers.',
      'What moves from one flower to another?',
    ],
    collectEvidence: [
      'Collect only clues you observed in the garden.',
      'Find one clue about visits, one about pollen, and one about seed pods.',
    ],
    compare: ['Compare the flowers that were visited with the ones that were not.'],
    apply: ['Use the evidence to connect the pollinator route to the flowering patch.'],
  },
});

export const POLLINATOR_GARDEN_SLICE = {
  id: 'pollinator-garden',
  title: 'The Pollinator Garden',
  prerequisiteInvestigationId: 'washed-out-trail-1',
  productionReady: POLLINATOR_GARDEN_PRODUCTION_READY,
  investigation: POLLINATOR_GARDEN_INVESTIGATION,
} as const;
